import z from 'zod';

export type SortableContentType = 'accordion' | 'copy';
export type CopyType = 'paragraph' | 'ordered-list' | 'unordered-list';

export interface SortableContentDto {
  title?: string;
  type: SortableContentType;
  sortOrder: number;
}

export interface AccordionEntryDetailsDto {
  summary: string;
  details: string;
  sortOrder: number;
}

export interface AccordionContentDto extends SortableContentDto {
  entries: AccordionEntryDetailsDto[];
}

export interface CopyBlockDto {
  title?: string;
  copy: string;
}

export interface CopyEntryDetailsDto {
  copyType: CopyType;
  title: string;
  copyBlocks: CopyBlockDto[];
  sortOrder: number;
}

export interface CopyContentDto extends SortableContentDto {
  entries: CopyEntryDetailsDto[];
  isHighlighted?: boolean;
}

export type SectionContentDto = AccordionContentDto | CopyContentDto;

export interface LearnMoreContentDto {
  title: string;
  sections: SectionContentDto[];
}

export const SectionContentSchema = z.object({
  title: z.string().optional(),
  type: z.enum(['accordion', 'copy']),
  sortOrder: z.number(),
  isHightlighted: z.boolean().optional(),
  entries: z.array(
    z.object({
      copyType: z
        .enum(['paragraph', 'ordered-list', 'unordered-list'])
        .optional(),
      title: z.string().optional(),
      summary: z.string().optional(),
      details: z.string().optional(),
      sortOrder: z.number().optional(),
      copyBlocks: z
        .array(
          z.object({
            title: z.string().optional(),
            copy: z.string().optional(),
          })
        )
        .optional(),
    })
  ),
});

export const LearnMoreContentSchema = z.object({
  title: z.string(),
  sections: z.array(SectionContentSchema).min(1),
  selectedLearnMoreRecordId: z.number().default(-1),
});

export type LearnMoreContentData = z.infer<typeof LearnMoreContentSchema>;

export const DEFAULT_LEARN_MORE_CONTENT: LearnMoreContentDto = {
  title: '',
  sections: [],
};

export const DEFAULT_ACCORDION_ENTRY: AccordionEntryDetailsDto = {
  summary: '',
  details: '',
  sortOrder: 1,
};

export const DEFAULT_COPY_BLOCK: CopyBlockDto = {
  title: '',
  copy: '',
};

export const DEFAULT_COPY_ENTRY: CopyEntryDetailsDto = {
  copyType: 'paragraph',
  title: '',
  copyBlocks: [{ ...DEFAULT_COPY_BLOCK }],
  sortOrder: 1,
};
